import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Building2, CheckCircle2, X, Loader2 } from "lucide-react";

export default function TransferRequestPage() {
    const [params] = useSearchParams();
    const token = params.get("token");
    const initialAction = params.get("action");
    const [req, setReq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [decided, setDecided] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) { setError("Token mancante"); setLoading(false); return; }
        api.get(`/listing-transfers/by-token/${token}`)
            .then((r) => setReq(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Richiesta non trovata"))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (!loading && req && req.status === "pending" && (initialAction === "accept" || initialAction === "decline") && !decided && !busy) {
            decide(initialAction);
        }
        // eslint-disable-next-line
    }, [loading, req, initialAction]);

    const decide = async (action) => {
        setBusy(true); setError(null);
        try {
            const { data } = await api.post(`/listing-transfers/by-token/${token}/decide`, { action });
            setDecided(data.status);
        } catch (e) { setError(e?.response?.data?.detail || "Errore"); }
        finally { setBusy(false); }
    };

    if (loading) return <div className="pt-32 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Caricamento...</div>;
    if (error && !req) return <div className="pt-32 max-w-xl mx-auto px-6 text-center"><Building2 size={32} className="text-gold mx-auto mb-4" /><p className="text-ink-400">{error}</p></div>;

    const finalStatus = decided || req?.status;
    const accepted = finalStatus === "accepted";
    const declined = finalStatus === "declined";
    const alreadyDecided = req?.status !== "pending";

    return (
        <div data-testid="transfer-page" className="pt-32 pb-24 max-w-2xl mx-auto px-6">
            <div className="border border-white/10 p-10 bg-ink-900/40">
                <div className="flex items-center gap-3 mb-4">
                    <Building2 size={20} className="text-gold" />
                    <span className="label-eyebrow">Trasferimento annuncio</span>
                </div>
                <h1 className="font-serif text-3xl text-ink-50">Proposta di gestione</h1>
                <p className="text-sm text-ink-400 mt-4">L'agenzia <strong className="text-ink-100">{req?.agency_name}</strong> ti propone di gestire l'annuncio:</p>
                <p className="font-serif text-xl text-gold mt-3">{req?.property_title}</p>
                {req?.message && <blockquote className="mt-5 pl-4 border-l-2 border-gold/40 text-sm italic text-ink-100">{req.message}</blockquote>}

                <div className="mt-6 p-4 border border-amber-700/40 bg-amber-900/10 text-amber-200 text-xs">
                    <strong>Cosa cambia se accetti:</strong> l'annuncio passa sotto la gestione dell'agenzia. Loro potranno modificarlo, ricevere i contatti e rispondere ai potenziali acquirenti. I tuoi dati personali restano riservati.
                </div>

                {alreadyDecided && !decided && (
                    <div className="mt-6 p-4 bg-ink-900 border border-white/10 text-ink-400 text-sm">Questa proposta è già stata {req.status === "accepted" ? "accettata" : "rifiutata"}.</div>
                )}

                {(!finalStatus || finalStatus === "pending") && (
                    <div className="mt-8 flex gap-3">
                        <button data-testid="transfer-accept" onClick={() => decide("accept")} disabled={busy} className="flex-1 bg-gold text-black py-4 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2">
                            <CheckCircle2 size={16} /> Accetto trasferimento
                        </button>
                        <button data-testid="transfer-decline" onClick={() => decide("decline")} disabled={busy} className="flex-1 border border-white/15 text-ink-100 py-4 text-sm hover:border-red-400 hover:text-red-300 transition-colors flex items-center justify-center gap-2">
                            <X size={16} /> Rifiuto
                        </button>
                    </div>
                )}

                {accepted && <div data-testid="transfer-accepted" className="mt-8 p-5 bg-green-900/20 border border-green-700/40 text-green-200 text-sm">✓ Annuncio trasferito all'agenzia. Riceverai una conferma via email.</div>}
                {declined && <div data-testid="transfer-declined" className="mt-8 p-5 bg-red-900/20 border border-red-700/40 text-red-200 text-sm">✗ Proposta rifiutata. L'annuncio resta sotto la tua gestione.</div>}

                <Link to="/" className="inline-block mt-8 text-xs text-gold hover:underline">← Vai al sito</Link>
            </div>
        </div>
    );
}
